import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight,
  CheckCircle,
  MapPin,
  Mail,
  Phone,
  Lightbulb,
  Shield,
  Users,
  Zap,
  ArrowUp,
  Globe,
  Code,
  Settings,
  Wrench,
  Monitor
} from 'lucide-react';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Sections
import Team from './components/sections/Team';
import CTABanner from './components/sections/CTABanner';
import Blog from './components/sections/Blog';
import Newsletter from './components/sections/Newsletter';
import Container from './components/common/Container';

// For map integration - OpenStreetMap with Leaflet
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const AboutPage = () => {
  // State for scroll position and mobile menu
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // References for scroll animations
  const storyRef = useRef(null);
  const missionRef = useRef(null);
  const visionRef = useRef(null);
  const valuesRef = useRef(null);
  const resourcesRef = useRef(null);
  const contactRef = useRef(null);
  const headerRef = useRef(null);
  
  // Map configuration
  const mapCenter = [44.119371, 15.231365]; // Galo Industries location
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Monitor scroll position with throttling
  useEffect(() => {
    let timeoutId = null;
    const handleScroll = () => {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          setScrolled(window.scrollY > 50);
          timeoutId = null;
        }, 100); // Throttle to 100ms
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  
  // Core values from landing page
  const coreValues = [
    {
      title: "Engineering Expertise",
      description: "Deep knowledge of automation systems.",
      icon: <Settings className="w-6 h-6" />,
      color: "from-cyan-400 to-blue-500"
    },
    {
      title: "Tailored Execution",
      description: "We adapt to your production, not the other way around.",
      icon: <Wrench className="w-6 h-6" />,
      color: "from-blue-400 to-indigo-500"
    },
    {
      title: "Real-World Thinking",
      description: "We build systems that work on-site, not just on paper.",
      icon: <Zap className="w-6 h-6" />,
      color: "from-cyan-400 to-teal-500"
    },
    {
      title: "Continuous Curiosity",
      description: "We apply useful technologies without the hype.",
      icon: <Lightbulb className="w-6 h-6" />,
      color: "from-indigo-400 to-purple-500"
    }
  ];

  // Expertise areas based on landing page services
  const expertiseAreas = [
    {
      title: "Software Solutions",
      icon: <Code className="w-6 h-6" />,
      description: "Custom software for quality control and assembly lines."
    },
    {
      title: "Design & Implementation",
      icon: <Wrench className="w-6 h-6" />,
      description: "Expert design and commissioning of industrial equipment."
    },
    {
      title: "Innovative Solutions",
      icon: <Lightbulb className="w-6 h-6" />,
      description: "Smart upgrades that enhance production without complete overhauls."
    }
  ];

  // Approach steps
  const approachSteps = [
    {
      number: "01",
      title: "Process Analysis",
      description: "We assess your production and identify key automation opportunities."
    },
    {
      number: "02",
      title: "Custom Solution Design",
      description: "We develop tailored solutions that integrate with your existing systems."
    },
    {
      number: "03",
      title: "Implementation & Optimization",
      description: "Professional deployment with testing to ensure peak performance."
    }
  ];
  
  // Resources
  const resourceItems = [
    {
      title: "Download Company Profile",
      description: "Find more about our projects and what are we doing",
      icon: <Monitor className="w-5 h-5" />,
      link: "/content/Galo-Company-Profile.pdf"
    },
    {
      title: "Download Industry 4.0 & Smart Factory Report",
      description: "Find out more about Industry 4.0 and Smart Factories in our report",
      icon: <CheckCircle className="w-5 h-5" />,
      link: "/content/Galo-Industry4.pdf"
    }
  ];
  
  // Animation on scroll with optimized performance
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    
    const refs = [storyRef, missionRef, visionRef, valuesRef, resourcesRef, contactRef];
    
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>About Galo Industries | Industrial Automation Solutions | Zadar, Croatia</title>
        <meta
          name="description"
          content="Galo Industries provides industrial automation services including software development for quality control, feeding and welding systems, plus design and implementation of industrial equipment."
        />
        <meta name="keywords" content="industrial automation, quality control, feeding systems, welding systems, industrial equipment, Galo Industries, Zadar" />
        <link rel="canonical" href="https://galo-industries.com/about" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="theme-color" content="#0891b2" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://galo-industries.com/about" />
        <meta property="og:title" content="About Galo Industries | Industrial Automation Solutions" />
        <meta property="og:description" content="Galo Industries provides industrial automation services including software development for quality control, feeding and welding systems, plus design and implementation of industrial equipment." />
        <meta property="og:image" content="https://galo-industries.com/img/galo-team.webp" />
        <meta property="og:site_name" content="Galo Industries" />
        
        {/* Structured data for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Galo Industries",
            "url": "https://galo-industries.com",
            "logo": "https://galo-industries.com/img/logo.png",
            "description": "Industrial automation specialists providing efficient software solutions, design, development, implementation and commissioning of industrial equipment.",
            "slogan": "Born in Factory. Raised on Technology. Growing into Innovation.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Šibenska ulica 4B",
              "addressLocality": "Zadar",
              "addressRegion": "Zadar County",
              "postalCode": "23000",
              "addressCountry": "HR"
            },
            "foundingDate": "2016",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+385-98-199-1813",
              "contactType": "customer service",
              "email": "info@galo-industries.com"
            }
          })}
        </script>
      </Helmet>

      <Header />

      
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 md:py-32 overflow-hidden">
        {/* Gradient background with enhanced layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 z-0">
          {/* Dot pattern overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.15)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
          
          {/* Animated blurred shapes */}
          <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-300/10 to-blue-300/5 rounded-full blur-2xl animate-pulse-slow hidden lg:block"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/10 to-indigo-300/5 rounded-full blur-2xl animate-pulse-slow hidden lg:block" style={{animationDelay: '2s'}}></div>
        </div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center text-blue-100 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">About Us</span>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Left content - text */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                About <span className="relative inline-block">
                  <span className="text-cyan-200">Galo Industries</span>
                  <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-cyan-400/80 to-blue-300/60 rounded-full"></span>
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-blue-100 mb-6 sm:mb-8 leading-relaxed max-w-3xl">
                Founded in 2016, we deliver industrial automation across Europe with engineering precision and practical experience.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8 sm:mb-10">
                <a href="#story" className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-blue-700 rounded-lg shadow-lg font-medium transition-all duration-300 hover:shadow-cyan-500/20 inline-flex items-center text-sm sm:text-base">
                  Our Story
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </a>
                <a href="#team" className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 text-white rounded-lg font-medium transition-all duration-300 inline-flex items-center text-sm sm:text-base">
                  Meet Our Team
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </a>
              </div>
              
              {/* Key stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white/10 backdrop-blur-md p-3 sm:p-5 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-xl sm:text-3xl font-bold text-white mb-1">50+</div>
                  <div className="text-cyan-100 text-xs sm:text-sm">Projects</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-3 sm:p-5 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-xl sm:text-3xl font-bold text-white mb-1">9+</div>
                  <div className="text-cyan-100 text-xs sm:text-sm">Years</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-3 sm:p-5 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-xl sm:text-3xl font-bold text-white mb-1">10+</div>
                  <div className="text-cyan-100 text-xs sm:text-sm">Partners</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-3 sm:p-5 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-xl sm:text-3xl font-bold text-white mb-1">16+</div>
                  <div className="text-cyan-100 text-xs sm:text-sm">Team</div>
                </div>
              </div>
            </div>
            
            {/* Right content - image */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/30 transform transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10">
                {/* Multiple image layers with gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/70 via-blue-800/50 to-cyan-900/30 mix-blend-multiply"></div>
                
                <img 
                  src="/img/galo-team.webp" 
                  alt="Galo Industries team" 
                  className="w-full h-full object-cover"
                  loading="eager"
                  width="600"
                  height="400"
                />
                
                {/* Location badge */}
                <div className="absolute top-4 sm:top-6 left-4 sm:left-6 inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs">
                  <MapPin className="w-3 h-3 mr-1" />
                  Zadar, Croatia
                </div>
                
                {/* Small card overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-900/90 to-transparent p-4 sm:p-6">
                  <div className="flex items-center">
                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mr-3">
                      <Shield className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-bold text-sm sm:text-base">Est. 2016</div>
                      <div className="text-blue-100 text-xs sm:text-sm">Automation specialists</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Company Story Section */}
      <section id="story" className="py-16 md:py-20 lg:py-24 bg-white" ref={storyRef}>
        <Container>
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center story-content">
            {/* Left content */}
            <div className="lg:col-span-6">
              <div className="mb-6">
                <div className="inline-flex items-center mb-3">
                  <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                  <span className="text-cyan-700 font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Our Story</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
                  Born in <span className="relative">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Factory</span>
                    <span className="absolute -bottom-1 left-0 w-full h-[5px] bg-gradient-to-r from-cyan-400/30 to-blue-600/30 rounded-full"></span>
                  </span>
                </h2>
              </div>
              
              <p className="text-slate-700 text-base sm:text-lg mb-4 sm:mb-6 leading-relaxed">
                Since 2016, Galo Industries has delivered practical automation solutions that optimize manufacturing efficiency. Our approach comes from hands-on experience.
              </p>
              
              <p className="text-slate-700 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                What sets us apart is our real manufacturing experience across Europe. We've worked directly on production lines for major automotive and manufacturing clients.
              </p>
              
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-xl p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700 mb-2">Since 2016</div>
                  <div className="text-slate-800 font-medium text-sm sm:text-base">Automation Experts</div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-xl p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700 mb-2">Europe-Wide</div>
                  <div className="text-slate-800 font-medium text-sm sm:text-base">Project Delivery</div>
                </div>
              </div>
            </div>
            
            {/* Right image with expertise card */}
            <div className="lg:col-span-6 relative">
              <div className="bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
                {/* Main image */}
                <div className="relative">
                  <img 
                    src="/img/engineering.jpeg" 
                    alt="Industrial automation engineering by Galo Industries" 
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/20 to-transparent mix-blend-multiply"></div>
                </div>
                
                {/* Expertise section */}
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4 flex items-center">
                    <Shield className="w-4 sm:w-5 h-4 sm:h-5 text-cyan-600 mr-2" />
                    Core Expertise
                  </h3>
                  
                  <ul className="space-y-3 sm:space-y-4">
                    {expertiseAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="bg-cyan-50 p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3 text-cyan-700">
                          {area.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-0.5 text-sm sm:text-base">{area.title}</h4>
                          <p className="text-slate-600 text-xs sm:text-sm">{area.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Services Section */}
      <section id="mission" className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50" ref={missionRef}>
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16 mission-heading">
            <div className="inline-flex items-center mb-3">
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              <span className="text-cyan-700 font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Our Services</span>
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
              Industrial <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Automation</span> Solutions
            </h2>
            <p className="text-base sm:text-lg text-slate-600">
              From simple systems to complete automation
            </p>
          </div>
          
          {/* Service cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mission-cards">
            <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 mission-card group">
              {/* Card top color band */}
              <div className="h-2 bg-gradient-to-r from-cyan-400 to-blue-600"></div>
              
              <div className="p-6 sm:p-8">
                <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mb-4 sm:mb-6 shadow-lg transform transition-transform duration-300 group-hover:rotate-3">
                  <Code className="w-6 sm:w-8 h-6 sm:h-8" />
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">Software Solutions</h3>
                
                <p className="text-slate-600 text-sm sm:text-base">
                  Custom software that optimizes manufacturing and ensures quality. Designed for your specific processes and needs.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 mission-card group">
              {/* Card top color band */}
              <div className="h-2 bg-gradient-to-r from-cyan-400 to-blue-600"></div>
              
              <div className="p-6 sm:p-8">
                <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mb-4 sm:mb-6 shadow-lg transform transition-transform duration-300 group-hover:rotate-3">
                  <Wrench className="w-6 sm:w-8 h-6 sm:h-8" />
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">Design & Implementation</h3>
                
                <p className="text-slate-600 text-sm sm:text-base">
                  Complete engineering services from concept to commissioning. We handle the entire automation process.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 mission-card group">
              {/* Card top color band */}
              <div className="h-2 bg-gradient-to-r from-cyan-400 to-blue-600"></div>
              
              <div className="p-6 sm:p-8">
                <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mb-4 sm:mb-6 shadow-lg transform transition-transform duration-300 group-hover:rotate-3">
                  <Lightbulb className="w-6 sm:w-8 h-6 sm:h-8" />
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">Innovative Solutions</h3>
                
                <p className="text-slate-600 text-sm sm:text-base">
                  Smart upgrades that boost efficiency without replacing entire systems. Focused on integration and optimization.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Methodology Section */}
      <section id="vision" className="py-16 md:py-20 lg:py-24 bg-white relative overflow-hidden" ref={visionRef}>
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-24 right-0 w-96 h-96 bg-gradient-to-br from-cyan-50 to-transparent rounded-full opacity-70 hidden lg:block"></div>
          <div className="absolute -bottom-24 left-0 w-96 h-96 bg-gradient-to-tl from-blue-50 to-transparent rounded-full opacity-70 hidden lg:block"></div>
        </div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center vision-content">
            {/* Left side - Vision statement */}
            <div className="lg:col-span-5 vision-statement">
              <div className="inline-flex items-center mb-3">
                <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                <span className="text-cyan-700 font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Our Approach</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 leading-tight">
                Tailored <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Approach</span>
              </h2>
              
              <div className="bg-gradient-to-br from-slate-800 to-blue-900 rounded-xl p-6 sm:p-8 shadow-xl mb-6 sm:mb-8">
                <p className="text-lg sm:text-xl text-white mb-4 sm:mb-5 leading-relaxed">
                  "We adapt to your production — not the other way around. We blend technical precision with factory floor reality for solutions that work reliably."
                </p>
                <div className="border-t border-white/20 pt-4 sm:pt-5 text-right">
                  <p className="text-cyan-200 font-medium text-sm sm:text-base">Galo Industries</p>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <a href="#contact" className="inline-flex items-center text-cyan-700 hover:text-blue-800 transition-colors duration-300 font-medium text-sm sm:text-base">
                  <span>Discover our capabilities</span>
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </a>
              </div>
            </div>
            
            {/* Right side - Engineering Process */}
            <div className="lg:col-span-7 vision-approach">
              <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-5 sm:p-6 lg:p-10">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8">Our Implementation Process</h3>
                
                <div className="space-y-6 sm:space-y-10">
                  {approachSteps.map((step, idx) => (
                    <div key={idx} className="flex group">
                      <div className="flex-shrink-0 mr-4 sm:mr-6">
                        <div className="w-10 sm:w-14 h-10 sm:h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white text-base sm:text-lg font-bold transition-transform duration-300 group-hover:scale-110">
                          {step.number}
                        </div>
                        {idx < approachSteps.length - 1 && (
                          <div className="h-full w-0.5 bg-slate-200 mx-auto mt-4"></div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">{step.title}</h4>
                        <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-slate-200">
                  <div className="flex items-center">
                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-blue-50 flex items-center justify-center mr-3 sm:mr-4">
                      <Shield className="w-5 sm:w-6 h-5 sm:h-6 text-blue-700" />
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-900 text-sm sm:text-base">Production-Ready Solutions</h5>
                      <p className="text-slate-600 text-xs sm:text-sm">Built for consistent performance in real manufacturing conditions.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lg:hidden mt-6 sm:mt-8">
                <a href="#contact" className="inline-flex items-center text-cyan-700 hover:text-blue-800 transition-colors duration-300 font-medium text-sm sm:text-base">
                  <span>Discover our capabilities</span>
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </a>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Core Values Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50" ref={valuesRef}>
        <Container>
          <div className="text-center mb-10 md:mb-16 values-heading">
            <div className="inline-flex items-center mb-3">
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              <span className="text-cyan-700 font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Core Values</span>
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
              Principles That <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Drive Us</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Our core values define how we approach automation solutions.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 values-grid">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-xl border border-slate-100 h-full flex flex-col transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 value-card group"
              >
                {/* Icon with gradient background */}
                <div className={`w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r ${value.color} flex items-center justify-center text-white mb-4 sm:mb-6 shadow-lg transform transition-transform duration-300 group-hover:rotate-3`}>
                  {value.icon}
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">{value.title}</h3>
                <p className="text-slate-600 flex-grow text-sm sm:text-base">{value.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>
      
      {/* Resources Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-blue-600 to-cyan-500 text-white" ref={resourcesRef}>
        <Container>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center resources-content">
            <div>
              <div className="inline-flex items-center mb-3">
                <div className="h-px w-8 sm:w-12 bg-white/40"></div>
                <span className="text-white font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Resources</span>
                <div className="h-px w-8 sm:w-12 bg-white/40"></div>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 leading-tight">
                Automation Resources
              </h2>
              <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 leading-relaxed">
                Access guides and case studies on industrial automation best practices.
              </p>
              
              <div className="grid gap-3 sm:gap-4">
                {resourceItems.map((item, idx) => (
                  <a 
                    href={item.link} 
                    key={idx}
                    className="flex items-center p-3 sm:p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-white/20 flex items-center justify-center mr-3 sm:mr-4">
                      {item.icon}
                    </div>
                    <div>
                      <div className="font-bold text-white text-sm sm:text-base">{item.title}</div>
                      <div className="text-blue-100 text-xs sm:text-sm">{item.description}</div>
                    </div>
                    <ArrowRight className="ml-auto text-blue-100 w-4 sm:w-5 h-4 sm:h-5" />
                  </a>
                ))}
              </div>
              
              <div className="mt-6 sm:mt-8">
                <a 
                  href="/contact"
                  className="inline-flex items-center px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-blue-700 rounded-lg shadow-lg font-medium hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
                >
                  Contact Our Team
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-cyan-500/30 rounded-2xl transform rotate-6 scale-95 blur-xl"></div>
              <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/30">
                <div className="aspect-[4/3] p-4">
                  <div className="grid grid-cols-2 gap-4 h-full">
                    <div className="grid gap-4">
                      <div className="rounded-xl overflow-hidden bg-white/10 shadow-lg">
                        <img 
                          src="/img/innovation.jpg" 
                          alt="Industrial automation engineering" 
                          className="w-full h-full object-cover"
                          loading="lazy"
                          width="300"
                          height="200"
                        />
                      </div>
                      <div className="rounded-xl bg-gradient-to-br from-white/10 to-white/5 p-4 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-xl sm:text-2xl font-bold">50+</div>
                          <div className="text-xs sm:text-sm">Projects</div>
                        </div>
                      </div>
                    </div>
                    <div className="grid gap-4">
                      <div className="rounded-xl bg-gradient-to-br from-white/10 to-white/5 p-4 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-xl sm:text-2xl font-bold">9+</div>
                          <div className="text-xs sm:text-sm">Years</div>
                        </div>
                      </div>
                      <div className="rounded-xl overflow-hidden bg-white/10 shadow-lg">
                        <img 
                          src="/img/robot2.jpeg" 
                          alt="Galo Industries team" 
                          className="w-full h-full object-cover"
                          loading="lazy"
                          width="300"
                          height="200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Team Section - Using the existing component */}
      <Team />
      
      {/* Location & Contact Section with OpenStreetMap */}
      <section id="contact" className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50" ref={contactRef}>
        <Container>
          <div className="text-center mb-10 md:mb-16 contact-heading">
            <div className="inline-flex items-center mb-3">
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              <span className="text-cyan-700 font-semibold uppercase text-xs sm:text-sm tracking-wider mx-3">Contact Us</span>
              <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
              Connect with <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Galo Industries</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Based in Zadar, Croatia, we serve manufacturing clients across Europe.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-6 md:gap-8 contact-content">
            {/* Contact info cards */}
            <div className="lg:col-span-5 space-y-6 md:space-y-8">
              {/* Address card */}
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-start">
                  <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center text-cyan-700 mr-4 sm:mr-5 shadow-md">
                    <MapPin className="w-6 sm:w-7 h-6 sm:h-7" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">Headquarters</h3>
                    <p className="text-base sm:text-lg text-slate-700">
                      Šibenska ulica 4B<br />
                      23000, Zadar<br />
                      Croatia, EU
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Contact methods */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center text-cyan-700 mr-3 sm:mr-4 shadow-md">
                      <Mail className="w-5 sm:w-6 h-5 sm:h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">Email Us</h3>
                      <a href="mailto:info@galo-industries.com" className="text-cyan-700 hover:text-blue-800 transition-colors text-xs sm:text-sm">
                        info@galo-industries.com
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center text-cyan-700 mr-3 sm:mr-4 shadow-md">
                      <Phone className="w-5 sm:w-6 h-5 sm:h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">Call Us</h3>
                      <a href="tel:+385981991813" className="text-cyan-700 hover:text-blue-800 transition-colors text-xs sm:text-sm">
                        +385 98 199 1813
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl shadow-xl p-5 sm:p-6 text-white">
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Ready to Optimize Your Manufacturing?</h3>
                <p className="text-blue-100 mb-5 sm:mb-6 text-sm sm:text-base">
                  Contact us to discuss your automation needs.
                </p>
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  <a 
                    href="/contact"
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-white text-blue-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 font-medium text-sm sm:text-base"
                  >
                    Start Your Project
                  </a>
                  <a 
                    href="/services"
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-500/30 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-blue-500/50 transition-all duration-300 font-medium text-sm sm:text-base"
                  >
                    View Services
                  </a>
                </div>
              </div>
            </div>
            
            {/* OpenStreetMap Integration */}
            <div className="lg:col-span-7 map-container">
              <div className="bg-white rounded-xl overflow-hidden shadow-xl h-full min-h-[300px] sm:min-h-[400px] border border-slate-100">
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  scrollWheelZoom={false}
                  className="h-full w-full"
                  style={{ minHeight: '400px' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={mapCenter}>
                    <Popup>
                      <div className="text-center p-2">
                        <h4 className="font-bold text-slate-900 mb-1">Galo Industries</h4>
                        <p className="text-sm text-slate-600">Industrial Automation</p>
                        <p className="text-xs text-slate-500">Šibenska ulica 4B, Zadar</p>
                      </div>
                    </Popup>
                  </Marker>
                  <ZoomControl position="bottomright" />
                  
                  {/* Map overlay */}
                  <div className="absolute top-4 sm:top-6 right-4 sm:right-6 max-w-xs bg-white rounded-lg shadow-lg p-3 sm:p-4 border border-slate-100 z-[1000]">
                    <div className="flex items-center mb-1 sm:mb-2">
                      <Globe className="w-4 sm:w-5 h-4 sm:h-5 text-cyan-700 mr-1.5 sm:mr-2" />
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base">European Coverage</h4>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600">
                      Serving manufacturing clients across Europe from our Zadar headquarters.
                    </p>
                  </div>
                </MapContainer>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* CTA Banner - using existing component */}
      <CTABanner
        title="Ready to improve your industrial processes?"
        body="Let us help optimize your manufacturing operations."
        primary={{ label: 'Contact Us', href: '/contact' }}
        secondary={{ label: 'View Services', href: '/services' }}
      />
      
      {/* Latest blog posts - using existing component */}
      <Blog />
      
      {/* Newsletter - using existing component */}
      <Newsletter />
      
      {/* Site footer - using existing component */}
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-4 sm:w-5 h-4 sm:h-5" />
        </button>
      )}
      
      <style jsx>{`
        /* Animation for slow pulse - optimized */
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.3; }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
        
        /* Scroll animation classes - optimized */
        .story-content,
        .mission-heading,
        .mission-cards,
        .vision-content,
        .values-heading,
        .values-grid,
        .contact-heading,
        .contact-content,
        .resources-content,
        .map-container {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
          will-change: opacity, transform;
        }
        
        .is-visible .story-content,
        .is-visible .mission-heading,
        .is-visible .values-heading,
        .is-visible .vision-content,
        .is-visible .contact-heading,
        .is-visible .resources-content {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.1s;
        }
        
        .is-visible .mission-cards,
        .is-visible .values-grid,
        .is-visible .contact-content,
        .is-visible .map-container {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.2s;
        }
        
        /* Card animations - optimized */
        .is-visible .mission-card,
        .is-visible .value-card {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.4s forwards;
        }
        
        /* Staggered delays */
        .is-visible .mission-card:nth-child(1),
        .is-visible .value-card:nth-child(1) {
          animation-delay: 0.1s;
        }
        
        .is-visible .mission-card:nth-child(2),
        .is-visible .value-card:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .is-visible .mission-card:nth-child(3),
        .is-visible .value-card:nth-child(3) {
          animation-delay: 0.3s;
        }
        
        .is-visible .value-card:nth-child(4) {
          animation-delay: 0.4s;
        }
        
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Optimize for reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-slow {
            animation: none;
          }
          
          .story-content,
          .mission-heading,
          .mission-cards,
          .vision-content,
          .values-heading,
          .values-grid,
          .contact-heading,
          .contact-content,
          .resources-content,
          .map-container {
            transition: none;
            opacity: 1;
            transform: translateY(0);
          }
          
          .is-visible .mission-card,
          .is-visible .value-card {
            animation: none;
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Leaflet specific styles to fix z-index issues */
        .leaflet-container {
          z-index: 1;
        }
        
        .leaflet-top, .leaflet-bottom {
          z-index: 10;
        }
        
        .leaflet-control {
          z-index: 10;
        }
      `}</style>
    </>
  );
};

export default AboutPage;